import { useState, forwardRef } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { tv } from 'tailwind-variants';
import DOMPurify from 'dompurify';

import { useMediaQuery } from '@/hooks/use-media-query';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { Button } from '@/components/ui/button/button.component';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.component';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer.component';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.component';

export type SelectItem = {
  id: string | number;
  value: string;
  label: string;
};

type SelectProps = {
  items: SelectItem[];
  selectedItem: SelectItem | null;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
  isLoading?: boolean;
  onChange: (item: SelectItem | null) => void;
  dataTestId?: string;
};

function Label({
  isLoading,
  placeholder,
  selectedItem,
}: {
  selectedItem: SelectProps['selectedItem'];
  placeholder: SelectProps['placeholder'];
  isLoading: SelectProps['isLoading'];
}) {
  if (isLoading) {
    return <span className="text-gray-400">Carregando...</span>;
  }

  if (selectedItem) {
    return <span className="text-gray-900 truncate">{selectedItem.label}</span>;
  }

  return <span className="text-gray-400">{placeholder}</span>;
}

const selectStyle = tv({
  slots: {
    buttonTrigger: [
      'bg-white hover:bg-gray-50 justify-between shrink-0 transition-transform duration-200',
      'text-base shadow-tooltip border h-12 outline-none focus:border-blue-300',
    ],
  },

  variants: {
    isLoading: {
      true: {
        buttonTrigger: 'bg-gray-100 justify-start',
      },
    },
    open: {
      true: {
        buttonTrigger: 'border-blue-300',
      },
      false: {
        buttonTrigger: 'border-gray-300',
      },
    },
    selectedItem: {
      true: {
        buttonTrigger: 'border-gray-300',
      },
    },
    hasError: {
      true: {
        buttonTrigger: 'border-danger-600 focus:border-danger-600',
      },
      false: {
        buttonTrigger: 'focus:border-blue-300',
      },
    },
  },

  compoundVariants: [
    {
      open: true,
      selectedItem: false,
      class: {
        buttonTrigger: 'border-blue-300',
      },
    },
    {
      open: false,
      hasError: true,
      class: {
        buttonTrigger: 'border-danger-600',
      },
    },
  ],
  defaultVariants: {
    open: false,
    selectedItem: false,
    hasError: false,
  },
});

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      items,
      selectedItem,
      placeholder = 'Selecione uma opção',
      searchPlaceholder = 'Digite aqui',
      hasError,
      isLoading,
      onChange,
      dataTestId: propDataTestId,
      ...props
    },
    ref,
  ) => {
    const dataTestId = propDataTestId || 'button-container';

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const isDesktop = useMediaQuery('(min-width: 640px)');
    const debouncedInputValue = useDebounceValue(inputValue, 300);
    const { buttonTrigger } = selectStyle();

    const filteredItems = items.filter((item) => item.label.toLowerCase().includes(debouncedInputValue.toLowerCase()));

    const handleSelect = (value: string) => {
      const selected = items.find((item) => item.value === value) || null;
      onChange(selected);
      setOpen(false);
    };

    if (isDesktop) {
      return (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              {...props}
              ref={ref}
              data-testid={dataTestId}
              variant="outline"
              className={buttonTrigger({ open, selectedItem: !!selectedItem, hasError, isLoading })}
              endIcon={open ? faChevronUp : faChevronDown}
              endIconClassName="text-gray-400"
              isLoading={isLoading}
            >
              <Label isLoading={isLoading} selectedItem={selectedItem} placeholder={placeholder} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 bg-white" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                data-testid="input"
                className="py-2"
                placeholder={searchPlaceholder}
                onChangeCapture={(e: { currentTarget: { value: string } }) => {
                  const sanitizedValue = DOMPurify.sanitize(e.currentTarget.value);
                  setInputValue(sanitizedValue);
                }}
              />
              <CommandList className="h-full">
                {inputValue.length > 0 && !filteredItems.length && (
                  <CommandEmpty data-testid="empty-state" className="p-4 text-center">
                    <span className="text-base text-gray-900">
                      {inputValue.length > 2 ? 'Nenhum resultado encontrado' : 'Digite ao menos 3 caracteres'}
                    </span>
                  </CommandEmpty>
                )}
                {filteredItems.length > 0 && (
                  <CommandGroup className="h-full">
                    {filteredItems.map((item) => (
                      <CommandItem
                        key={`key-command-group-${item.id}${item.label}${item.value}`}
                        value={item.value}
                        onSelect={handleSelect}
                        className="cursor-pointer"
                      >
                        {item.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <Button
            {...props}
            ref={ref}
            data-testid={dataTestId}
            variant="outline"
            className={`bg-white hover:bg-gray-50 justify-between shrink-0 transition-transform duration-200 text-base shadow-tooltip border h-12
            outline-none focus:border-blue-300
          ${open && !selectedItem ? 'border-blue-300' : 'border-gray-300'} 
          ${selectedItem ? 'border-gray-300 text-gray-900' : 'text-gray-400'}
          ${hasError && !open ? 'border-danger-600 focus:border-danger-600' : ''}
          `}
          >
            {selectedItem ? <>{selectedItem.label}</> : <>{placeholder}</>}
          </Button>
        </DrawerTrigger>
        <DrawerContent className="flex min-h-[50%]">
          <DrawerHeader>
            <DrawerTitle className="flex text-lg">Selecione</DrawerTitle>
          </DrawerHeader>
          <div className="border-t">
            <Command shouldFilter={false}>
              <CommandInput
                data-testid="input"
                className="py-2"
                placeholder={searchPlaceholder}
                onChangeCapture={(e: { currentTarget: { value: string } }) => {
                  const sanitizedValue = DOMPurify.sanitize(e.currentTarget.value);
                  setInputValue(sanitizedValue);
                }}
              />
              <CommandList className="h-full">
                {inputValue.length > 0 && !filteredItems.length && (
                  <CommandEmpty data-testid="empty-state" className="p-4 text-center">
                    <span className="text-base text-gray-900">
                      {inputValue.length > 2 ? 'Nenhum resultado encontrado' : 'Digite ao menos 3 caracteres'}
                    </span>
                  </CommandEmpty>
                )}
                {filteredItems.length > 0 && (
                  <CommandGroup className="h-full">
                    {filteredItems.map((item) => (
                      <CommandItem
                        key={`key-select-item-${item.id}${item.value}${item.label}`}
                        value={item.value}
                        onSelect={handleSelect}
                        className="cursor-pointer"
                      >
                        {item.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </div>
        </DrawerContent>
      </Drawer>
    );
  },
);

Select.displayName = 'Select';

export default Select;
